import React from 'react';

import { WarningOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import styled from 'styled-components';

import Button from 'components/lib/Button';

import { TRANSCRIPT_REQUIREMENT_ACTION } from '../constants';
import { ShopRequestTranscriptRequirementAction, ShopRequestTranscriptRequirementType } from '../types';

const { TextArea } = Input;

const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  border: 1px solid var(--error-red-border-light);
  border-radius: 8px;
  background: var(--error-red-background);
  color: var(--error-red-dark);
`;

const AlertBody = styled.div`
  padding: 12px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Title = styled.span`
  color: var(--error-red-title);
  font-family: var(--font-bold);
`;

const Description = styled.span`
  display: block;
  color: var(--error-red-dark);
  font-size: 12px;
  padding-left: 22px;
`;

const StrongText = styled.span`
  font-family: var(--font-bold);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid var(--negative-orange);
  margin-top: 12px;
  padding-top: 12px;
`;

const ReasonContainer = styled.div`
  margin-top: 16px;
`;

const ReasonLabel = styled.span`
  display: block;
  color: var(--error-red-dark);
  font-size: 12px;
  font-family: var(--font-bold);
  margin-bottom: 8px;
`;

const ReasonInput = styled(TextArea)`
  &&& {
    border-color: var(--error-red-border-light);
    border-radius: 4px;
    color: var(--text-primary);

    &:hover,
    &:focus {
      border-color: var(--error-red-border);
      box-shadow: none;
    }
  }
`;

const ConfirmButton = styled(Button)`
  min-width: 198px;
  height: 32px;
  padding: 0 18px;
  background: var(--positive-green-action);
  border-color: var(--positive-green-action);
  color: var(--white);
  box-shadow: none;

  &:not(:disabled):not(.ant-btn-disabled):hover,
  &:not(:disabled):not(.ant-btn-disabled):focus {
    background: var(--positive-green-action-hover) !important;
    border-color: var(--positive-green-action-hover) !important;
    color: var(--white) !important;
  }
`;

const CannotProvideButton = styled(Button)`
  min-width: 202px;
  height: 32px;
  padding: 0 18px;
  border-color: var(--error-red-border);
  color: var(--error-red-dark);
  background: transparent;
  box-shadow: none;

  &:not(:disabled):not(.ant-btn-disabled):hover,
  &:not(:disabled):not(.ant-btn-disabled):focus {
    border-color: var(--error-red-hover) !important;
    color: var(--error-red-dark) !important;
    background: var(--error-red-background) !important;
  }
`;

const CancelButton = styled(Button)`
  flex: 1;
  height: 32px;
  padding: 0 18px;
  color: var(--text-secondary);
  box-shadow: none;
`;

const SubmitReasonButton = styled(Button)`
  flex: 1;
  height: 32px;
  padding: 0 18px;
  background: var(--negative-red-action);
  border-color: var(--negative-red-action);
  color: var(--white);
  box-shadow: none;

  &:not(:disabled):not(.ant-btn-disabled):hover,
  &:not(:disabled):not(.ant-btn-disabled):focus {
    background: var(--negative-red-action-hover) !important;
    border-color: var(--negative-red-action-hover) !important;
    color: var(--white) !important;
  }
`;

type Props = {
  requirement: ShopRequestTranscriptRequirementType;
  activeAction: ShopRequestTranscriptRequirementAction | null;
  isResolved: boolean;
  isSubmitting: boolean;
  showReasonForm: boolean;
  reason: string;
  trimmedReason: string;
  onConfirm: () => void;
  onShowReasonForm: () => void;
  onReasonChange: (value: string) => void;
  onCancelReason: () => void;
  onSubmitReason: () => void;
};

const EditShopRequestModalTranscriptRequirementAlert = ({
  requirement,
  activeAction,
  isResolved,
  isSubmitting,
  showReasonForm,
  reason,
  trimmedReason,
  onConfirm,
  onShowReasonForm,
  onReasonChange,
  onCancelReason,
  onSubmitReason,
}: Props) => {
  const isSubmitReasonDisabled = !trimmedReason || isSubmitting;
  const isConfirming = activeAction === TRANSCRIPT_REQUIREMENT_ACTION.CONFIRM;
  const isRejecting = activeAction === TRANSCRIPT_REQUIREMENT_ACTION.REJECT;

  return (
    <Container>
      <AlertBody>
        <Header>
          <WarningOutlined />
          <Title>Call Transcript Required</Title>
        </Header>
        <Description>
          This community is in {requirement.state_name} (single-party consent state).{' '}
          <StrongText>You must upload a call transcript</StrongText> when entering data for this shop.
        </Description>
        {!isResolved && (
          <Actions>
            <ConfirmButton
              size="small"
              disabled={isSubmitting}
              loading={isConfirming}
              onClick={!isSubmitting ? onConfirm : undefined}
            >
              I Acknowledge &amp; Confirm
            </ConfirmButton>
            <CannotProvideButton
              size="small"
              disabled={isSubmitting}
              onClick={!isSubmitting ? onShowReasonForm : undefined}
            >
              I Cannot Provide Transcript
            </CannotProvideButton>
          </Actions>
        )}
        {showReasonForm && !isResolved && (
          <ReasonContainer>
            <ReasonLabel>Please provide a reason why you cannot provide a transcript:</ReasonLabel>
            <ReasonInput
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
              placeholder="Enter reason..."
              rows={3}
            />
            <Actions>
              <CancelButton size="small" disabled={isSubmitting} onClick={!isSubmitting ? onCancelReason : undefined}>
                Cancel
              </CancelButton>
              <SubmitReasonButton
                size="small"
                disabled={isSubmitReasonDisabled}
                loading={isRejecting}
                onClick={!isSubmitReasonDisabled ? onSubmitReason : undefined}
              >
                Submit Reason
              </SubmitReasonButton>
            </Actions>
          </ReasonContainer>
        )}
      </AlertBody>
    </Container>
  );
};

export default EditShopRequestModalTranscriptRequirementAlert;
